import React, { useEffect, useState } from "react";
import styled from "styled-components";
import Logo from "../sharable/Logo";
import { Link, useNavigate, useOutletContext } from "react-router-dom";
import { devices } from "../../Devices";
import WarnModal from "../sharable/WarnModal";
import { formatDate, formatPlan } from "../../services/Utils";
import Notification from "../sharable/Notification";
import PaymentsTable from "../sharable/PaymentsTable";

const Container = styled.div`
  padding: 2rem 3.6rem;
  min-height: 100vh;
  background: #fff;
  @media ${devices.mobileL} {
    padding: 1rem 0.5rem;
  }
`;

const Header = styled.header`
  display: flex;
  align-items: center;
  justify-content: start;
`;

const Title = styled.h1`
  font-size: 2.4rem;
  font-weight: 600;
  margin: 2.5rem 0 1.5rem 0;
  text-align: center;
  color: #22223b;
  @media ${devices.mobileL} {
    font-size: 1.6rem;
    margin: 1.5rem 0 1rem 0;
  }
`;

const InfoBox = styled.div`
  background: #fbf1e6;
  border-radius: 1.2rem;
  padding: 1.5rem 2rem;
  margin-bottom: 2rem;
  display: flex;
  flex-direction: column;
  gap: 0.8rem;
  font-size: 1.2rem;
  color: #22223b;
  @media ${devices.mobileL} {
    padding: 1rem;
    font-size: 1rem;
  }
`;

const TableWrapper = styled.div`
  overflow-x: auto;
  margin-top: 2rem;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  background: #fff;
  font-size: 1.2rem;
  @media ${devices.mobileL} {
    font-size: 1rem;
    min-width: 480px;
  }
  th,
  td {
    padding: 0.8rem 1rem;
    text-align: left;
    border-bottom: 1px solid #eaeaea;
  }
  th {
    background: #fbf1e6;
    color: #22223b;
    font-weight: 600;
  }
`;

const StatusBadge = styled.span`
  padding: 0.2rem 0.8rem;
  border-radius: 1rem;
  font-size: 1rem;
  font-weight: 500;
  color: #fff;
  background: ${({ status }) =>
    status === "COMPLETED" || status === "ACTIVE"
      ? "#00D100"
      : status === "PENDING"
      ? "#fbbf24"
      : status === "CANCELED" || status === "INACTIVE"
      ? "#ff3333"
      : ""};
`;

const ChangePlanLink = styled(Link)`
  display: inline-block;
  margin-top: 1.5rem;
  color: #d97706;
  font-weight: 600;
  border-bottom: 1px solid #d97706;
  text-decoration: none;
  transition: border 0.2s;
  &:hover {
    border-bottom: 1px solid transparent;
  }
  width: fit-content;
`;

const CancelButton = styled.button`
  background: #ff3333;
  color: #fff;
  border: none;
  border-radius: 0.7rem;
  padding: 0.5rem 1.2rem;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  margin-top: 0.8rem;
  transition: background 0.2s;
  width: fit-content;
  &:hover {
    background: #e60000;
  }
`;

function formatPaymentMethod(method) {
  switch (method) {
    case "CREDIT_CARD_STRIPE":
      return "Cartão (Stripe)";
    case "PIX_EFI":
      return "Pix";
    case "PIX_MERCADO_PAGO":
      return "Pix";
    default:
      return method;
  }
}

const BASE_URL = process.env.REACT_APP_SERVER_BASE_URL;

export default function PaymentsHistoryView() {
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const { activeAccount, user, token, accessExpiryDate, accountStatus } =
    useOutletContext();
  const [showWarn, setShowWarn] = useState(false);
  const [notification, setNotification] = useState(null);
  const [showCancelWarn, setShowCancelWarn] = useState(false);
  useEffect(() => {
    async function fetchPayments() {
      setLoading(true);
      try {
        const res = await fetch(
          BASE_URL + "subscription/payments?email=" + user.email,
          {
            headers: {
              Authorization: token,
              "Content-Type": "application/json",
            },
          }
        );
        const data = await res.json();
        setPayments(data || []);
      } catch (err) {
        setPayments([]);
      }
      setLoading(false);
    }
    fetchPayments();
  }, []);

  const handleChangePlan = (e) => {
    e.preventDefault();
    if (activeAccount) {
      setShowWarn(true);
    } else {
      navigate("/planos");
    }
  };

  const handleConfirm = () => {
    setShowWarn(false);
    navigate("/planos");
  };

  const handleCancel = () => {
    setShowWarn(false);
  };

  const confirmCancelStripe = async () => {
    try {
      const res = await fetch(
        BASE_URL +
          `subscription/cancel?paymentId=${accountStatus.lastPaymentId}`,
        {
          method: "POST",
          headers: {
            Authorization: token,
            "Content-Type": "application/json",
          },
        }
      );
      if (res.status === 200) {
        setNotification({
          message: "Assinatura cancelada com sucesso!",
          type: "success",
        });
      } else {
        setNotification({
          message: "Falha ao cancelar assinatura.",
          type: "error",
        });
      }
    } catch {
      setNotification({
        message: "Falha ao cancelar assinatura.",
        type: "error",
      });
    }
    setTimeout(() => setNotification(null), 4000);
    setShowCancelWarn(false);
  };

  return (
    <Container>
      <Header>
        <Logo showGoBackButton={true} path="/encartes" />
      </Header>
      <Title>Histórico de Pagamentos</Title>
      {notification && (
        <Notification message={notification.message} type={notification.type} />
      )}
      <InfoBox>
        <div>
          <b>Plano atual:</b> {formatPlan(accountStatus.currentPlan)}
        </div>
        <div>
          <b>Expira em:</b>{" "}
          {accessExpiryDate ? formatDate(accessExpiryDate) : "-"}
        </div>
        <div>
          <b>Status da assinatura:</b>{" "}
          <StatusBadge status={activeAccount ? "ACTIVE" : "INACTIVE"}>
            {activeAccount ? "Ativa" : "Inativa"}
          </StatusBadge>
        </div>
        {activeAccount && (
          <div>
            <b>Renovação automática:</b>{" "}
            <StatusBadge
              status={accountStatus?.autoRenewalOn ? "ACTIVE" : "INACTIVE"}
            >
              {accountStatus?.autoRenewalOn ? "Habilitado" : "Desabilitado"}
            </StatusBadge>
          </div>
        )}
        {accountStatus.autoRenewalOn && (
          <>
            <CancelButton onClick={() => setShowCancelWarn(true)}>
              Cancelar assinatura
            </CancelButton>
            <WarnModal
              open={showCancelWarn}
              onConfirm={confirmCancelStripe}
              onCancel={() => setShowCancelWarn(false)}
              message="Tem certeza que deseja cancelar sua assinatura?"
            />
          </>
        )}
        <ChangePlanLink onClick={handleChangePlan}>
          {activeAccount ? "Alterar Plano" : "Escolher um plano"}
        </ChangePlanLink>
        <WarnModal
          open={showWarn}
          onConfirm={handleConfirm}
          onCancel={handleCancel}
          message={
            "Ao continuar com a alteração de plano, você perderá o acesso ao plano atual imediatamente."
          }
        />
      </InfoBox>
      <PaymentsTable payments={payments} loading={loading} />
    </Container>
  );
}
